import { Component } from '@theme/component';

const WATCHED_KEY = 'dc-reels-watched';
const SWIPE_DISTANCE = 50;

/**
 * "See it in action" reels (HANDOFF-7): tiles open a story-style player in a native modal
 * dialog. The <video> is created when a reel opens (nothing loads before that) and removed on
 * close. Plays muted inline; the middle of the video or the sound button unmutes. The left /
 * right thirds, a horizontal swipe or the arrow keys move between reels; a reel ending moves
 * to the next (the last one closes). Esc and ✕ close; focus returns to the tile natively.
 * Watched reels (localStorage) get a grey ring. Inside the customizer, reels of the selected
 * method move to the front (HANDOFF-9) and "Choose print / embroidery" sets that method.
 *
 * @typedef {{
 *   tiles: HTMLButtonElement[], dialog: HTMLDialogElement, player: HTMLElement, stage: HTMLElement,
 *   fills: HTMLElement[], nowPlaying: HTMLElement, soundButton: HTMLButtonElement,
 *   choose: HTMLButtonElement, prev?: HTMLButtonElement, next?: HTMLButtonElement
 * }} Refs
 * @extends {Component<Refs>}
 */
class DcReelsComponent extends Component {
  requiredRefs = ['tiles', 'dialog', 'player', 'stage', 'fills', 'nowPlaying', 'soundButton', 'choose'];

  #index = 0;
  #muted = true;
  /** @type {HTMLVideoElement | null} */
  #video = null;
  /** @type {number | undefined} */
  #frame;
  /** @type {{ x: number, y: number } | null} */
  #pointer = null;
  #swiped = false;
  /** @type {AbortController | undefined} */
  #abortController;
  /** @type {MutationObserver | undefined} */
  #methodObserver;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const { dialog, player } = this.refs;
    dialog.addEventListener('close', this.#teardown, { signal });
    dialog.addEventListener('keydown', this.#handleKeydown, { signal });
    player.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    player.addEventListener('pointerup', this.#handlePointerUp, { signal });
    // A swipe ends in a click on whatever is under the finger: swallow it
    player.addEventListener(
      'click',
      (event) => {
        if (!this.#swiped) return;
        this.#swiped = false;
        event.stopPropagation();
        event.preventDefault();
      },
      { capture: true, signal }
    );
    this.#markWatched();

    // The customizer may not be upgraded yet: watch the element itself
    const customizer = /** @type {HTMLElement | null} */ (this.closest('dc-customizer-component'));
    if (customizer) {
      this.#sortByMethod(customizer.dataset.method);
      this.#methodObserver = new MutationObserver(() => this.#sortByMethod(customizer.dataset.method));
      this.#methodObserver.observe(customizer, { attributes: true, attributeFilter: ['data-method'] });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#methodObserver?.disconnect();
    this.#teardown();
  }

  /** Tiles in their current order (they move when the method changes). */
  get #tiles() {
    return /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('.dc-reels__tile')]);
  }

  /**
   * Opens the player at the clicked tile (or at an index).
   * @param {Event | string | number} target
   */
  open(target) {
    const { dialog } = this.refs;
    let index = Number(target) || 0;
    if (target instanceof Event && target.target instanceof Element) {
      const tile = target.target.closest('.dc-reels__tile');
      index = Math.max(0, this.#tiles.indexOf(/** @type {HTMLButtonElement} */ (tile)));
    }
    if (!dialog.open) dialog.showModal();
    this.#show(index);
  }

  /**
   * Reels of the selected method first, then reels for any method (e.g. Fabric), then the
   * other method's, each group in Position order (HANDOFF-10: Print → Printing, Heat press,
   * Fabric, Machine, Close-up).
   * @param {string | undefined} method
   */
  #sortByMethod(method) {
    const list = this.querySelector('.dc-reels__list');
    if (!list || this.refs.dialog.open) return;
    const items = [...list.children];
    items.forEach((item, i) => {
      if (!(item instanceof HTMLElement)) return;
      if (item.dataset.order === undefined) item.dataset.order = String(i);
    });
    const rank = (/** @type {Element} */ item) => {
      const tileMethod = item.querySelector('.dc-reels__tile')?.getAttribute('data-method');
      const group = !method ? 0 : tileMethod === method ? 0 : !tileMethod ? 1 : 2;
      return group * 1000 + Number(/** @type {HTMLElement} */ (item).dataset.order);
    };
    const sorted = [...items].sort((a, b) => rank(a) - rank(b));
    if (sorted.every((item, i) => item === items[i])) return;
    list.append(...sorted);
    list.scrollLeft = 0;
  }

  close() {
    this.refs.dialog.close();
  }

  next() {
    if (this.#index < this.#tiles.length - 1) this.#show(this.#index + 1);
    else this.close();
  }

  previous() {
    if (this.#index > 0) this.#show(this.#index - 1);
    else if (this.#video) this.#video.currentTime = 0;
  }

  toggleSound() {
    this.#muted = !this.#muted;
    if (this.#video) {
      this.#video.muted = this.#muted;
      if (!this.#muted && this.#video.paused) this.#video.play().catch(() => {});
    }
    this.refs.soundButton.setAttribute('aria-pressed', String(!this.#muted));
  }

  /** Sets the reel's method on the customizer, then closes the player. */
  chooseMethod() {
    const method = this.#tiles[this.#index]?.dataset.method;
    const customizer = this.#customizer;
    if (!method || !customizer) return;
    customizer.setMethod(method);
    this.close();
  }

  /**
   * Clicking the backdrop (the dialog element itself, around the 9:16 box) closes.
   * @param {MouseEvent} event
   */
  handleDialogClick(event) {
    if (event.target === this.refs.dialog) this.close();
  }

  /** @returns {(HTMLElement & { setMethod: (method: string) => void }) | null} */
  get #customizer() {
    const customizer = this.closest('dc-customizer-component');
    return customizer && 'setMethod' in customizer ? /** @type {any} */ (customizer) : null;
  }

  /** @param {number} index */
  #show(index) {
    const { stage, fills, nowPlaying, choose } = this.refs;
    const tiles = this.#tiles;
    const tile = tiles[index];
    if (!tile) return;
    this.#index = index;

    this.#stopVideo();
    const video = document.createElement('video');
    video.src = tile.dataset.src ?? '';
    if (tile.dataset.poster) video.poster = tile.dataset.poster;
    video.muted = this.#muted;
    video.playsInline = true;
    video.autoplay = true;
    video.preload = 'auto';
    video.setAttribute('aria-label', tile.dataset.title ?? '');
    video.addEventListener('ended', () => this.next());
    stage.replaceChildren(video);
    this.#video = video;
    // Desktop: never wider than the video itself (low-res reels aren't upscaled; HANDOFF-10)
    const width = Number(tile.dataset.width);
    this.refs.dialog.style.setProperty('--dc-reel-max', width ? `${Math.max(width, 464)}px` : 'none');
    video.play().catch(() => {});

    const subtitle = this.dataset.subtitle;
    nowPlaying.textContent = subtitle ? `${tile.dataset.title} · ${subtitle}` : tile.dataset.title ?? '';
    fills.forEach((fill, i) => {
      fill.style.width = i < index ? '100%' : '0%';
    });

    // "Choose embroidery" only when the page offers it and it isn't already chosen
    const method = tile.dataset.method;
    const customizer = this.#customizer;
    const offered = method && customizer?.querySelector(`button.dc-cz__mcard[data-method="${method}"]`);
    choose.hidden = !offered || customizer?.dataset.method === method;
    if (!choose.hidden) choose.textContent = choose.dataset[method === 'print' ? 'labelPrint' : 'labelEmbroidery'] ?? '';

    this.#remember(tile.dataset.handle);
    this.#tick();
  }

  #tick = () => {
    const video = this.#video;
    const fill = this.refs.fills[this.#index];
    if (video && fill && video.duration) fill.style.width = `${(video.currentTime / video.duration) * 100}%`;
    this.#frame = requestAnimationFrame(this.#tick);
  };

  #stopVideo() {
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = undefined;
    if (!this.#video) return;
    this.#video.pause();
    this.#video.removeAttribute('src');
    this.#video.load();
    this.#video.remove();
    this.#video = null;
  }

  #teardown = () => {
    this.#stopVideo();
    this.refs.stage?.replaceChildren();
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    const rtl = getComputedStyle(this).direction === 'rtl';
    if (event.key === 'ArrowRight') rtl ? this.previous() : this.next();
    else if (event.key === 'ArrowLeft') rtl ? this.next() : this.previous();
    else return;
    event.preventDefault();
  };

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    this.#pointer = { x: event.clientX, y: event.clientY };
    this.#swiped = false;
  };

  /** @param {PointerEvent} event */
  #handlePointerUp = (event) => {
    if (!this.#pointer) return;
    const dx = event.clientX - this.#pointer.x;
    const dy = event.clientY - this.#pointer.y;
    this.#pointer = null;
    if (Math.abs(dx) < SWIPE_DISTANCE || Math.abs(dx) < Math.abs(dy)) return;
    this.#swiped = true;
    const rtl = getComputedStyle(this).direction === 'rtl';
    // Swiping left (finger moves left) shows the next reel
    if (dx < 0 !== rtl) this.next();
    else this.previous();
  };

  /** @returns {string[]} */
  #watched() {
    try {
      const list = JSON.parse(localStorage.getItem(WATCHED_KEY) ?? '[]');
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  }

  /** @param {string | undefined} handle */
  #remember(handle) {
    if (!handle) return;
    const list = this.#watched();
    if (!list.includes(handle)) {
      list.push(handle);
      try {
        localStorage.setItem(WATCHED_KEY, JSON.stringify(list.slice(-50)));
      } catch {
        // Storage can be full or blocked (private mode); the ring just stays coloured
      }
    }
    this.#markWatched();
  }

  #markWatched() {
    const list = this.#watched();
    for (const tile of this.#tiles) tile.toggleAttribute('data-watched', list.includes(tile.dataset.handle ?? ''));
  }
}

if (!customElements.get('dc-reels-component')) {
  customElements.define('dc-reels-component', DcReelsComponent);
}
